import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Trophy, ArrowLeft, Upload, FileCheck2, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { WEST_AFRICA_COUNTRIES } from "../lib/fcfa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MB = 10;

export default function Register() {
  const [form, setForm] = useState({
    name: "",
    city: "",
    country: "Bénin",
    sport: "Football",
    admin_code: "",
    president_label: "Président",
    contact_email: "",
    contact_phone: "",
  });
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState(null); // {url, path, filename, size, content_type}
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const handle = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (max ${MAX_MB} Mo)`);
      return;
    }
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(f.type)) {
      toast.error("Format accepté : PDF, JPG, PNG");
      return;
    }
    setFile(f);
    setUploaded(null);
  };

  const onUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/public/registration-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(data);
      toast.success("Document téléversé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.admin_code.length < 4) { toast.error("Le code admin doit faire au moins 4 caractères"); return; }
    if (!uploaded) { toast.error("Veuillez téléverser le récépissé de décision avant d'envoyer la demande"); return; }
    if (!form.contact_email || !form.contact_phone) { toast.error("Email et téléphone du président sont requis"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/clubs/register`, {
        ...form,
        recipisse_url: uploaded.path,
        recipisse_filename: uploaded.filename,
      });
      setSuccess(data);
      toast.success("Demande envoyée avec succès");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] flex items-center justify-center p-4">
        <div className="bg-white border border-[#E0E0E0] rounded-lg p-8 max-w-md w-full text-center" data-testid="register-success">
          <CheckCircle2 className="w-16 h-16 text-[#2E7D32] mx-auto mb-3" />
          <h2 className="font-display text-2xl font-extrabold">Demande envoyée !</h2>
          <p className="text-sm text-[#616161] mt-3">{success.message}</p>
          <div className="bg-[#F4F5F7] rounded p-3 mt-4 text-xs text-left space-y-1">
            <p><strong>Club :</strong> {success.club?.name}</p>
            <p><strong>Identifiant :</strong> <span className="font-mono">{success.club?.slug}</span></p>
            <p className="text-[#616161]">Notez cet identifiant — vous en aurez besoin pour vous connecter une fois validé.</p>
          </div>
          <Link to="/" className="inline-block mt-5 text-sm text-[#C62828] hover:underline">Retour à l&apos;accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col bg-[#9E1B1B] text-white p-12 relative overflow-hidden">
        <Link to="/" className="inline-flex items-center gap-2 text-white/85 hover:text-white text-sm" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>
        <div className="mt-auto">
          <div className="w-12 h-12 rounded-md bg-[#FBC02D] flex items-center justify-center mb-6">
            <Trophy className="w-6 h-6 text-[#9E1B1B]" />
          </div>
          <p className="text-xs tracking-overline text-[#FBC02D] mb-4">Inscription Club</p>
          <h1 className="font-display text-5xl font-extrabold leading-tight">
            Inscription officielle des clubs sportifs.
          </h1>
          <p className="mt-6 text-white/75 text-base max-w-md">
            Pour garantir l&apos;authenticité de la plateforme, chaque club doit être validé par l&apos;administrateur sur la base du <strong>récépissé de décision</strong> officiel.
          </p>
          <div className="mt-6 flex items-center gap-2 text-sm text-[#FBC02D]">
            <ShieldCheck className="w-5 h-5" />
            Validation sous 24 à 48 heures
          </div>
        </div>
        <div className="mt-auto text-xs text-white/40">© WSDK Connect</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-white overflow-y-auto">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-4" data-testid="register-form">
          <div className="lg:hidden mb-2">
            <Link to="/" className="inline-flex items-center gap-2 text-[#616161] text-sm">
              <ArrowLeft className="w-4 h-4" /> Retour
            </Link>
          </div>
          <div>
            <p className="text-xs tracking-overline text-[#C62828] font-semibold mb-2">Demande d&apos;inscription</p>
            <h2 className="font-display text-3xl font-extrabold">Créer mon club</h2>
            <p className="text-sm text-[#616161] mt-1">Une fois validée, vous recevrez un email à l&apos;adresse fournie.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="name">Nom officiel du club *</Label>
            <Input id="name" required value={form.name} onChange={(e) => handle("name", e.target.value)}
              placeholder="Ex: AS Cotonou" data-testid="register-name-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="city">Ville</Label>
              <Input id="city" value={form.city} onChange={(e) => handle("city", e.target.value)}
                placeholder="Cotonou" data-testid="register-city-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Pays</Label>
              <Select value={form.country} onValueChange={(v) => handle("country", v)}>
                <SelectTrigger data-testid="register-country-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEST_AFRICA_COUNTRIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Sport principal</Label>
            <Select value={form.sport} onValueChange={(v) => handle("sport", v)}>
              <SelectTrigger data-testid="register-sport-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Football", "Basketball", "Handball", "Volleyball", "Athlétisme", "Rugby", "Autre"].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Contact info */}
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="contact_email">Email du président *</Label>
              <Input id="contact_email" type="email" required value={form.contact_email}
                onChange={(e) => handle("contact_email", e.target.value)}
                placeholder="president@club.com" data-testid="register-email-input" />
              <p className="text-[10px] text-[#616161]">Reçoit le statut de la demande et les notifications.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact_phone">Téléphone *</Label>
              <Input id="contact_phone" required value={form.contact_phone}
                onChange={(e) => handle("contact_phone", e.target.value)}
                placeholder="+229XXXXXXXX" data-testid="register-phone-input" />
            </div>
          </div>

          {/* Récépissé upload */}
          <div className="space-y-2 border-l-4 border-[#FBC02D] pl-3 py-2 bg-[#FBC02D]/5">
            <Label className="flex items-center gap-1.5 font-semibold">
              <FileCheck2 className="w-4 h-4" /> Récépissé de décision *
            </Label>
            <p className="text-[11px] text-[#616161]">
              Document officiel délivré par l&apos;autorité administrative (préfecture, ministère, fédération...) attestant la création légale de votre club. Format : PDF, JPG ou PNG. Max {MAX_MB} Mo.
            </p>
            <div className="flex items-center gap-2">
              <label className="flex-1 cursor-pointer">
                <input type="file" accept=".pdf,image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" data-testid="register-recipisse-file" />
                <div className="border border-dashed border-[#9E9E9E] rounded px-3 py-2 text-sm text-[#616161] truncate hover:border-[#C62828] hover:bg-[#C62828]/5">
                  {file ? <><FileCheck2 className="w-3.5 h-3.5 inline mr-1" /> {file.name}</> : "Choisir un fichier"}
                </div>
              </label>
              {file && !uploaded && (
                <Button type="button" size="sm" onClick={onUpload} disabled={uploading} className="bg-[#FBC02D] hover:bg-[#F9A825] text-[#121212]" data-testid="register-recipisse-upload">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> Téléverser</>}
                </Button>
              )}
              {uploaded && (
                <span className="text-xs text-[#2E7D32] font-medium flex items-center gap-1" data-testid="register-recipisse-uploaded">
                  <CheckCircle2 className="w-4 h-4" /> Envoyé
                </span>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="admin_code">Code Président (mot de passe) *</Label>
            <Input id="admin_code" type="text" required minLength={4}
              value={form.admin_code} onChange={(e) => handle("admin_code", e.target.value)}
              placeholder="Au moins 4 caractères" data-testid="register-admincode-input" />
            <p className="text-[10px] text-[#616161]">Servira à vous connecter une fois la demande validée. Conservez-le précieusement.</p>
          </div>

          <Button type="submit" disabled={loading || !uploaded} data-testid="register-submit-btn"
            className="w-full bg-[#C62828] hover:bg-[#B71C1C] text-white h-11 disabled:opacity-60">
            {loading ? "Envoi…" : !uploaded ? "Téléversez d'abord le récépissé" : "Envoyer ma demande"}
          </Button>

          <p className="text-sm text-center text-[#616161]">
            Vous avez déjà un club validé ?{" "}
            <Link to="/login" className="text-[#C62828] hover:underline font-medium">Connexion</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
